import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown, Minus, ChevronDown, ChevronLeft, ChevronRight, Volume2, Square, Sparkles, Target, Compass, EyeOff, ArrowRight, Zap, Bell, History, ShieldAlert } from 'lucide-react';
import { TEAM_AVATARS } from '../../components/teamAvatars';
import { authFetch } from '../../lib/auth';

/* Rachel Executive Brief — one short message at a time. No walls of text.
   Card deck: surprise → yesterday → changed mind → what happened → why → priority → trends → decision. */

const CONF = {
  high: { es: 'confianza alta', en: 'high confidence', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  medium: { es: 'confianza media', en: 'medium confidence', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  low: { es: 'confianza baja', en: 'low confidence', cls: 'bg-white/10 text-white/50 border-white/15' },
};

function ConfBadge({ level, isEs }) {
  const c = CONF[level] || CONF.medium;
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ${c.cls}`}>
      {isEs ? c.es : c.en}
    </span>
  );
}

function greet(isEs) {
  const h = new Date().getHours();
  if (h < 12) return isEs ? 'Buenos días' : 'Good morning';
  if (h < 19) return isEs ? 'Buenas tardes' : 'Good afternoon';
  return isEs ? 'Buenas noches' : 'Good evening';
}

const TDIR = {
  up: { Icon: TrendingUp, cls: 'text-emerald-300' },
  down: { Icon: TrendingDown, cls: 'text-red-300' },
  flat: { Icon: Minus, cls: 'text-white/40' },
};

function Skeleton() {
  return (
    <div className="animate-pulse space-y-3" data-testid="rachel-brief-skeleton">
      <div className="h-3 w-40 rounded bg-white/10" />
      <div className="h-6 w-3/4 rounded bg-white/[0.08]" />
      <div className="h-3 w-1/2 rounded bg-white/[0.06]" />
    </div>
  );
}

export default function RachelExecutiveBrief({ isEs }) {
  const [b, setB] = useState(null);
  const [err, setErr] = useState(false);
  const [step, setStep] = useState(0);
  const [detail, setDetail] = useState(false);
  const [noiseOpen, setNoiseOpen] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const audioRef = useRef(null);
  const stopRef = useRef(false);
  const rachel = TEAM_AVATARS.find((a) => a.key === 'rachel');

  useEffect(() => {
    authFetch('/admin/exec/executive-brief')
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(setB).catch(() => setErr(true));
    return () => { stopRef.current = true; if (audioRef.current) audioRef.current.pause(); };
  }, []);

  const pick = (o, short = false) => {
    if (!o) return '';
    if (short) return (isEs ? o.short_es : o.short_en) || (isEs ? o.es : o.en) || '';
    return (isEs ? o.es : o.en) || '';
  };

  /* Build the card deck — short message first, full text as optional detail */
  const cards = useMemo(() => {
    if (!b) return [];
    const out = [];
    if (b.surprise?.found) {
      (b.surprise.items || []).slice(0, 2).forEach((it, i) => out.push({
        id: `surprise-${i}`, testid: i === 0 ? 'rachel-surprise' : `rachel-surprise-${i}`,
        Icon: Bell, tone: 'text-amber-300',
        label: isEs ? 'Mientras no mirabas' : 'While you were away',
        msg: pick(it, true), full: pick(it), conf: it.confidence,
      }));
    }
    if (b.yesterday_review) {
      out.push({
        id: 'yesterday', testid: 'rachel-yesterday-review', Icon: History, tone: 'text-white/50',
        label: isEs ? '¿Funcionó lo de ayer?' : "Did yesterday work?",
        msg: pick(b.yesterday_review, true), full: pick(b.yesterday_review), conf: b.yesterday_review.confidence,
        badge: b.yesterday_review.verdict === 'worked'
          ? { txt: isEs ? '✓ funcionó' : '✓ it worked', cls: 'bg-emerald-500/15 text-emerald-300' }
          : { txt: isEs ? 'sin cambio' : 'no change', cls: 'bg-white/10 text-white/50' },
      });
    }
    if (b.disagreement) {
      out.push({
        id: 'disagreement', testid: 'rachel-disagreement', Icon: ShieldAlert, tone: 'text-red-300',
        label: isEs ? 'Cambio de opinión' : 'I changed my mind',
        msg: pick(b.disagreement, true), full: pick(b.disagreement), conf: b.disagreement.confidence,
      });
    }
    out.push({
      id: 'happened', testid: 'rachel-what-happened', Icon: Sparkles, tone: 'text-[#F77F11]',
      label: isEs ? '¿Qué pasó?' : 'What happened?',
      msg: pick(b.what_happened, true), full: pick(b.what_happened), conf: b.what_happened?.confidence,
    });
    (b.why_it_matters || []).slice(0, 2).forEach((w, i) => out.push({
      id: `why-${i}`, testid: i === 0 ? 'rachel-why-matters' : `rachel-why-${i}`,
      Icon: Compass, tone: 'text-[#F77F11]',
      label: isEs ? '¿Por qué importa?' : 'Why it matters',
      msg: pick(w, true), full: pick(w), conf: w.confidence,
    }));
    out.push({
      id: 'priority', testid: 'rachel-one-priority', Icon: Target, tone: 'text-[#F77F11]', highlight: true,
      label: isEs ? 'La prioridad — solo una' : 'The priority — just one',
      msg: pick(b.one_priority, true), full: pick(b.one_priority), conf: b.one_priority?.confidence,
    });
    if (b.trends?.length) out.push({ id: 'trends', testid: 'rachel-trends', Icon: TrendingUp, tone: 'text-[#F77F11]', label: isEs ? '¿Cómo cambia el negocio?' : 'How the business is changing', trends: b.trends });
    out.push({
      id: 'decision', testid: 'rachel-one-decision', Icon: Zap, tone: 'text-[#F77F11]', highlight: true,
      label: isEs ? 'Una decisión' : 'One decision',
      msg: pick(b.one_decision, true), full: pick(b.one_decision), conf: b.one_decision?.confidence,
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b, isEs]);

  const go = (n) => { setStep(Math.max(0, Math.min(cards.length - 1, n))); setDetail(false); };

  const stopVoice = () => {
    stopRef.current = true;
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setSpeaking(false);
  };

  const narrate = async () => {
    if (speaking) { stopVoice(); return; }
    if (!b) return;
    const lines = [
      `${greet(isEs)}. ${isEs ? 'Ya revisé el negocio. Esto es lo que importa.' : 'I already reviewed the business. Here is what matters.'}`,
      ...cards.filter((cd) => cd.msg).map((cd) => cd.msg),
    ].filter((t) => t && t.length > 2).map((t) => t.slice(0, 590));
    stopRef.current = false;
    setSpeaking(true);
    const base = process.env.REACT_APP_BACKEND_URL;
    try {
      for (const text of lines) {
        if (stopRef.current) break;
        const r = await fetch(`${base}/api/rest/present/voice`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('kx_token') ? { Authorization: `Bearer ${localStorage.getItem('kx_token')}` } : {}) },
          body: JSON.stringify({ text, lang: isEs ? 'es' : 'en', voice: 'shimmer' }),
        });
        if (!r.ok || stopRef.current) break;
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => {
          const a = new Audio(url);
          audioRef.current = a;
          a.onended = () => { URL.revokeObjectURL(url); resolve(); };
          a.onerror = () => { URL.revokeObjectURL(url); resolve(); };
          a.play().catch(resolve);
        });
      }
    } finally {
      setSpeaking(false);
      audioRef.current = null;
    }
  };

  if (err) return null;
  const card = cards[step];

  return (
    <section data-testid="rachel-executive-brief" className="rounded-2xl overflow-hidden relative"
      style={{ background: 'radial-gradient(1200px 400px at 10% -10%, rgba(247,127,17,0.14), transparent 55%), radial-gradient(900px 380px at 95% 110%, rgba(247,127,17,0.08), transparent 60%), #131416', border: '1px solid rgba(255,255,255,0.09)' }}>
      <div className="p-5 sm:p-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src={rachel?.img} alt="Rachel" className="w-11 h-11 rounded-full object-cover border-2 border-[#F77F11]/50" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#F77F11]">Rachel · Executive Brief</p>
              <p className="text-[14px] font-bold text-white leading-tight" data-testid="rachel-brief-greeting">
                {greet(isEs)}{b ? (isEs ? ' — ya revisé el negocio por ti.' : ' — I already reviewed the business for you.') : '…'}
              </p>
            </div>
          </div>
          {b && (
            <button onClick={narrate} data-testid="rachel-brief-listen"
              className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[11.5px] font-bold transition-colors ${speaking ? 'bg-red-500/20 text-red-300 border border-red-500/40' : 'bg-[#F77F11] text-white hover:bg-[#e57210]'}`}>
              {speaking ? <><Square className="w-3 h-3" /> {isEs ? 'Parar' : 'Stop'}</> : <><Volume2 className="w-3.5 h-3.5" /> {isEs ? 'Escuchar' : 'Listen'}</>}
            </button>
          )}
        </div>

        {/* One card at a time */}
        {!b ? <div className="mt-5 min-h-[130px]"><Skeleton /></div> : card && (
          <div className={`mt-5 rounded-xl border p-4 sm:p-5 min-h-[130px] flex flex-col justify-center transition-colors ${card.highlight ? 'border-[#F77F11]/40 bg-[#F77F11]/[0.08]' : 'border-white/10 bg-white/[0.03]'}`}
            data-testid={card.testid} key={card.id}>
            <p className="flex flex-wrap items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-white/40">
              <card.Icon className={`w-3 h-3 ${card.tone}`} /> {card.label}
              {card.badge && <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold normal-case tracking-normal ${card.badge.cls}`} data-testid="rachel-yesterday-verdict">{card.badge.txt}</span>}
              {card.conf && <ConfBadge level={card.conf} isEs={isEs} />}
            </p>
            {card.trends ? (
              <div className="mt-3 flex flex-wrap gap-2">
                {card.trends.map((t, i) => {
                  const D = TDIR[t.direction] || TDIR.flat;
                  return (
                    <span key={i} data-testid={`rachel-trend-${i}`} title={`${t.previous} → ${t.current}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-white/12 bg-white/[0.05] px-3 py-1.5 text-[11px] text-white/80">
                      <D.Icon className={`w-3.5 h-3.5 ${D.cls}`} />
                      {isEs ? t.metric_es : t.metric_en}
                      <b className={`tabular-nums ${D.cls}`}>{t.pct != null ? `${t.pct > 0 ? '+' : ''}${t.pct}%` : `${t.previous} → ${t.current}`}</b>
                    </span>
                  );
                })}
              </div>
            ) : (
              <>
                <p className="mt-2 text-[17px] sm:text-[19px] font-bold leading-snug text-white" data-testid="rachel-card-msg">{card.msg}</p>
                {card.full && card.full !== card.msg && (
                  <button onClick={() => setDetail(!detail)} data-testid="rachel-step-detail"
                    className="mt-2 self-start text-[10.5px] font-bold text-white/40 hover:text-[#F77F11] transition-colors inline-flex items-center gap-1">
                    {detail ? (isEs ? 'Ocultar detalle' : 'Hide detail') : (isEs ? 'Ver detalle' : 'See detail')}
                    <ChevronDown className={`w-3 h-3 transition-transform ${detail ? 'rotate-180' : ''}`} />
                  </button>
                )}
                {detail && <p className="mt-1.5 text-[12px] leading-relaxed text-white/60">{card.full}</p>}
              </>
            )}
          </div>
        )}

        {/* Pager: ‹ dots › */}
        {b && cards.length > 1 && (
          <div className="mt-3 flex items-center justify-center gap-3" data-testid="rachel-step-pager">
            <button onClick={() => go(step - 1)} disabled={step === 0} data-testid="rachel-step-prev"
              className="rounded-full border border-white/15 p-1.5 text-white/70 hover:border-[#F77F11] hover:text-white disabled:opacity-25 transition-colors">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-1.5">
              {cards.map((cd, i) => (
                <button key={cd.id} onClick={() => go(i)} data-testid={`rachel-step-dot-${i}`} aria-label={cd.label}
                  className={`rounded-full transition-all ${i === step ? 'w-5 h-1.5 bg-[#F77F11]' : 'w-1.5 h-1.5 bg-white/25 hover:bg-white/50'}`} />
              ))}
            </div>
            <button onClick={() => go(step + 1)} disabled={step === cards.length - 1} data-testid="rachel-step-next"
              className="rounded-full border border-white/15 p-1.5 text-white/70 hover:border-[#F77F11] hover:text-white disabled:opacity-25 transition-colors">
              <ChevronRight className="w-4 h-4" />
            </button>
            <span className="text-[10px] tabular-nums text-white/30">{step + 1}/{cards.length}</span>
          </div>
        )}

        {/* Footer: quick actions + ops noise (quiet) */}
        {b && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap gap-2" data-testid="rachel-quick-actions">
              <Link to="/admin/ai-os/autonomy" data-testid="rachel-qa-strategy"
                className="rounded-full border border-white/15 bg-white/[0.05] px-3 py-1.5 text-[11px] font-semibold text-white/85 hover:border-[#F77F11] hover:text-white transition-colors">
                {isEs ? 'Revisar estrategia' : 'Review strategy'}
              </Link>
              <Link to="/admin/ai-os/content" data-testid="rachel-qa-content"
                className="rounded-full border border-white/15 bg-white/[0.05] px-3 py-1.5 text-[11px] font-semibold text-white/85 hover:border-[#F77F11] hover:text-white transition-colors">
                {isEs ? 'Generar contenido' : 'Generate content'}
              </Link>
              <Link to="/admin/ai-os/autonomy" data-testid="rachel-qa-brief"
                className="rounded-full bg-[#F77F11] px-3 py-1.5 text-[11px] font-bold text-white hover:bg-[#e57210] transition-colors">
                {isEs ? 'Brief completo' : 'Full brief'}
              </Link>
            </div>
            <div data-testid="rachel-can-wait">
              <button onClick={() => setNoiseOpen(!noiseOpen)} data-testid="rachel-noise-toggle"
                className="flex items-center gap-1.5 text-[10.5px] font-semibold text-white/40 hover:text-white/70 transition-colors">
                <EyeOff className="w-3 h-3" /> {isEs ? 'Qué puede esperar' : 'What can wait'}
                <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold ${b.what_can_wait?.revenue_impacting ? 'bg-amber-500/20 text-amber-300' : 'bg-white/10 text-white/45'}`}>
                  {b.what_can_wait?.ops_events_24h || 0}
                </span>
                <ChevronDown className={`w-3 h-3 transition-transform ${noiseOpen ? 'rotate-180' : ''}`} />
              </button>
            </div>
          </div>
        )}
        {b && noiseOpen && (
          <div className="mt-2 rounded-xl border border-white/10 bg-black/20 p-3">
            <p className="text-[11.5px] text-white/60 leading-relaxed">{pick(b.what_can_wait)}</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {(b.what_can_wait?.ops_breakdown || []).map((o) => (
                <span key={o.type} className="rounded bg-white/[0.06] px-2 py-0.5 text-[9.5px] font-mono text-white/40">{o.type} ×{o.count}</span>
              ))}
            </div>
            <Link to="/admin/ai-os/autonomy" className="mt-2 inline-flex items-center gap-1 text-[10.5px] font-bold text-[#F77F11] hover:underline">
              {isEs ? 'Abrir Operations / Autonomía' : 'Open Operations / Autonomy'} <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        )}
        {b && !b.surprise?.found && (
          <p className="mt-2 text-[10.5px] text-white/30 italic" data-testid="rachel-quiet-night">
            {isEs ? b.surprise?.headline_es : b.surprise?.headline_en}
          </p>
        )}
      </div>
    </section>
  );
}
